import React, { useEffect, useState } from 'react';
import './FloatingScrollControls.scss';
import { IconChevronDown } from '../../../../../../components/Icons';
import classNames from 'classnames';

interface FloatingScrollControlsProps {
  onScrollToTop: () => void;
  onScrollToBottom: () => void;
  onRefresh?: () => void;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  bottomOffset?: string | number;
  rightOffset?: string | number;
  className?: string;
  showRefreshButton?: boolean;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  scrollThreshold?: number;
  chatComposerAvailable?: boolean;
  children?: React.ReactNode;
}

/**
 * A reusable component that provides floating scroll controls
 * to scroll to the top or bottom of a container.
 */
const FloatingScrollControls: React.FC<FloatingScrollControlsProps> = ({
  onScrollToTop,
  onScrollToBottom,
  onRefresh,
  position = 'bottom-right',
  bottomOffset = '20px',
  rightOffset = '20px',
  className = '',
  showRefreshButton = false,
  scrollContainerRef,
  scrollThreshold = 100,
  chatComposerAvailable = false,
  children,
}) => {
  // State to track scroll position
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(true);

  // Update button visibility based on scroll position
  useEffect(() => {
    if (!scrollContainerRef?.current) return;

    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      // Show scroll-to-top button only when scrolled down past threshold
      setShowScrollToTop(container.scrollTop > scrollThreshold);

      // Show scroll-to-bottom button only when not at the bottom
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 20; // 20px tolerance
      setShowScrollToBottom(!isAtBottom);
    };

    // Initial check
    handleScroll();

    const container = scrollContainerRef.current;
    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [scrollContainerRef, scrollThreshold]);

  return (
    <div className={`floating-scroll-controls ${className}`}>
      {/* Fixed position buttons */}
      <div
        className={classNames('floating-scroll-controls__buttons-wrapper', {
          'chat-composer-available': chatComposerAvailable,
        })}
      >
        <div
          className={`floating-scroll-controls__button-container ${showScrollToTop ? 'floating-scroll-controls__button-container--visible' : ''}`}
        >
          <button onClick={onScrollToTop} aria-label="Scroll to top" className="floating-scroll-controls__button">
            <div className="floating-scroll-controls__text-container">
              <IconChevronDown />
              KAI Report Analysis
            </div>
          </button>
        </div>
        <div
          className={`floating-scroll-controls__button-container ${showScrollToBottom ? 'floating-scroll-controls__button-container--visible' : ''}`}
        >
          <button onClick={onScrollToBottom} aria-label="Scroll to bottom" className="floating-scroll-controls__button">
            <div className="floating-scroll-controls__icon-container">
              <IconChevronDown />
            </div>
          </button>
        </div>
      </div>

      {/* The actual content */}
      {children}
    </div>
  );
};

export default FloatingScrollControls;
